'use client'

import React, { useEffect, useRef, useState } from 'react'

import { getPartnerName } from '@/app/api/envelope/helper'
import { FakeProgress } from '@/components'
import { PjFields, PfFields } from '@/components/Form/Fields'
import { consorcioData, consorcios } from '@/components/Form/Form.data'
import { MultiFileUpload } from '@/components/Form/MultiFileUpload'
import { usePartner } from '@/hooks'
import { postIdEnvelopDocSignToSalesForce, postLead } from '@/services'
import { logToServer } from '@/utils/logger'
import { useSearchParams } from 'next/navigation'
import { useParams } from 'next/navigation'

import { ModalConsumerUnit, FormData, InitialFields } from './'
import { ModalTos, ModalMinuta, ModalDocusign } from './Modal'
import useMultiFileUpload from './MultiFileUpload/MultiFileUpload.hook'

const Form = () => {
  const searchParams = useSearchParams()
  const progressBarRef = useRef<{ start: (task: () => Promise<void>) => void } | null>(null)
  const processingDataRef = useRef(null) // Referência para o formulário (opcional)
  const form = useRef(null) // Referência para o formulário (opcional)

  const { handleUpload, message, setMessage } = useMultiFileUpload()
  const campanha = searchParams.get('campanha')
  const { slug } = useParams()
  const { partner, lead_source } = usePartner(slug as string)

  const [loading, setLoading] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean | null>(null)
  const [disabled, setDisabled] = useState<boolean>(false)

  const [formData, setFormData] = useState<FormData>({
    ucs: {}
  })
  const [error, setError] = useState<boolean>(false)
  const [errorApi, setErrorApi] = useState<string>()
  const [processing, setProcessing] = useState<boolean>(false)
  const [files, setFiles] = useState<File[]>([])
  const [docusignUrl] = useState<string>()
  const [step, setStep] = useState<number>(1)
  const [leadId, setLeadId] = useState<string>('')
  const [loadingEnvelopDocusign, setLoadingEnvelopDocusign] = useState<boolean>(false)
  const [successDocusing, setSuccessDocusing] = useState<boolean | null>(null)

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (processing) {
      event.preventDefault()
      event.returnValue = 'Tem certeza que deseja sair? Alterações podem ser perdidas.'
    }
  }

  // Usado para inserir no payload o Cliente_MT e o Cliente_Grupo, pois ambos ficaram como hidden no formulário.
  const handleAddClienteMTAndClienteGrupo = (name: string) => {
    const id = name.replace(/\D/g, '')
    const cliente_MT = `Cliente_MT${id}`
    const cliente_Grupo = `Cliente_Grupo${id}`

    return { [cliente_MT]: 'Convencional', [cliente_Grupo]: 'BT' }
  }

  // Quando um campo é alterado, o valor é setado para o estado formData
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line prefer-const
    let { name, value, checked } = e.target
    setErrorApi('')
    if (name === 'tdo') {
      value = checked as unknown as string
    }

    // Há uma verificação inicial, para identificar as UCS e incluí-las como um objeto dentro de formData
    setFormData((prevFormData) => {
      // Verifica se o campo pertence ao objeto `ucs`
      if (
        name.startsWith('Cliente_UC') ||
        name.startsWith('Cliente_CM') ||
        name.startsWith('Cliente_Grupo') ||
        name.startsWith('Cliente_Subgrupo') ||
        name.startsWith('Cliente_MT')
      ) {
        return {
          ...prevFormData,
          ucs: {
            ...prevFormData.ucs,
            [name]: value,
            ...handleAddClienteMTAndClienteGrupo(name)
          }
        }
      } else {
        return {
          ...prevFormData,
          [name]: value
        }
      }
    })
  }

  // Quando o botão Próxima Etapa é clicado, cria novo lead no salesforce e avança para a etapa 2
  const handlePostLead = async () => {
    setLoading(true)
    setDisabled(true)
    setErrorApi('')

    // Informações do Consórcio Grupo BC
    const consorcio: consorcioData | null = formData.Cliente_Estado
      ? consorcios[formData.Cliente_Estado]
      : null

    // Tipo de Cliente
    let tipoCliente: string | undefined

    if (formData.Cliente_TIPO === 'Pessoa Física') {
      tipoCliente = formData.Cliente_TIPO
    }
    if (formData.Cliente_TIPO === 'Condomínio') {
      tipoCliente = 'Ente despersonalizado'
    }

    if (formData.Cliente_TIPO === 'Pessoa Jurídica') {
      tipoCliente = formData.Cliente_TIPO_Entidade
    }

    const payload: FormData = {
      ...formData,
      Cliente_RZC1: consorcio?.nome,
      Cliente_CONS: consorcio?.nome,
      Cliente_CNPJC1: consorcio?.cnpj,
      Cliente_ENDC1: consorcio?.endereco,
      Cliente_DESC: partner.descontoOfertado
        ? `${partner.descontoOfertado}%`
        : consorcio?.desconto_padrao,
      Cliente_TIPO: tipoCliente,
      Cliente_RESC2:
        formData.Cliente_Responsavel_Representante === 'Sim'
          ? formData.Cliente_REPC2
          : formData.Cliente_RESC2,
      Cliente_EMAILRESC2:
        formData.Cliente_Responsavel_Representante === 'Sim'
          ? formData.Cliente_EMAILREPC2
          : formData.Cliente_EMAILRESC2,
      partner_Id: partner?.id,
      partner_Name: slug ? getPartnerName(slug as string) : '',
      lead_source,
      campanha: campanha || ''
    }

    try {
      const { data } = await postLead(payload)

      if (data.leadId) {
        setLeadId(data.leadId)
        setStep(2)

        setTimeout(() => {
          const progressElement = form.current
          if (progressElement) {
            // Rola suavemente até o elemento
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            progressElement.scrollIntoView({
              behavior: 'smooth', // Rolagem suave
              block: 'start' // Alinha o topo da div ao topo da tela
            })

            // Aguarda a rolagem do scrollIntoView antes de ajustar o deslocamento
            setTimeout(() => {
              window.scrollBy({ top: -200, behavior: 'smooth' }) // Ajusta suavemente a posição
            }, 500) // Tempo suficiente para a rolagem inicial concluir
          }
        }, 100)
      }
    } catch (e) {
      const errorToLog = e instanceof Error ? e : new Error(String(e))
      logToServer('error', `Falha na criação do lead. LeadId: ${leadId}`, payload, errorToLog)
      setErrorApi('Serviço de leads indisponível. Tente novamente mais tarde.')
    } finally {
      setLoading(false)
      setDisabled(false)
    }
  }

  // Envia os arquivos para upload na nossa api
  const handlePostFiles = async () => {
    setLoading(true)
    setDisabled(true)
    setErrorApi('')
    setMessage('')

    const name =
      formData.Cliente_TIPO === 'Pessoa Jurídica' || formData.Cliente_TIPO === 'Condomínio'
        ? formData.Cliente_RZC2
        : formData.Cliente_REPC2

    try {
      await handleUpload(files, name as string, leadId)
      setProcessing(false) // Isso só será chamado após a finalização do progresso
      setSuccess(true) // Isso também será chamado após a finalização do progresso
    } catch (e) {
      const errorToLog = e instanceof Error ? e : new Error(String(e))
      logToServer('error', `Falha no upload de arquivos. LeadId: ${leadId}`, formData, errorToLog)
      setErrorApi(`Serviço de upload indisponível. Tente novamente mais tarde. Erro: ${e}`)
    } finally {
      setLoading(false)
      setDisabled(false)
    }
  }

  // Ao clicar no botão Próximo passo ou Quero aderir agora
  const handleSubmit = async (e: MouseEvent) => {
    e.preventDefault()
    switch (step) {
      case 1:
        return await handlePostLead()
      case 2:
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return document.getElementById('minuta_modal')?.showModal()
    }
  }

  // Essa função é chamada quando o usuário clica em Ok, Quero aderir agora no modal da minuta.
  // Após clicar ok, inicia o upload dos arquivos
  const handleOkPreviewMinuta = async () => await handlePostFiles()

  // Gera a URL da docusign e logo em seguida, chamada na api par gerar o lead.

  const handleGenerateUrl = async () => {
    const baseUrl = process.env.DOCUSIGN_BASE_URL
    const params = new URLSearchParams()

    if (lead_source === 'Parceiro') {
      // Preencher os parâmetros principais
      if (formData.Cliente_TIPO === 'Pessoa Jurídica' || formData.Cliente_TIPO === 'Condomínio') {
        params.append('PowerFormId', process.env.PJ_POWER_FORM_ID_BCPAR as string)
      }

      if (formData.Cliente_TIPO === 'Pessoa Física') {
        params.append('PowerFormId', process.env.PF_POWER_FORM_ID_BCPAR as string)
      }
    } else {
      // Preencher os parâmetros principais
      if (formData.Cliente_TIPO === 'Pessoa Jurídica' || formData.Cliente_TIPO === 'Condomínio') {
        params.append('PowerFormId', process.env.PJ_POWER_FORM_ID as string)
      }

      if (formData.Cliente_TIPO === 'Pessoa Física') {
        params.append('PowerFormId', process.env.PF_POWER_FORM_ID as string)
      }
    }

    params.append('env', process.env.DOCUSIGN_ENV as string)
    params.append('acct', process.env.DOCUSIGN_ACCT as string)
    params.append('v', process.env.DOCUSIGN_V as string)

    // Informações do Consórcio Grupo BC
    const consorcio: consorcioData | null = formData.Cliente_Estado
      ? consorcios[formData.Cliente_Estado]
      : null
    if (consorcio) {
      params.append('Cliente_RZC1', consorcio.nome || '')
      params.append('Cliente_CONS', consorcio.nome || '')
      params.append('Cliente_CNPJC1', consorcio.cnpj || '')
      params.append('Cliente_ENDC1', consorcio.endereco || '')
      params.append(
        'Cliente_DESC',
        (partner.descontoOfertado as string)
          ? (`${partner.descontoOfertado}%` as string)
          : (consorcio?.desconto_padrao as string)
      )
    }

    // Informações do Cliente
    const address = `${formData.street || ''}, ${formData.city || ''}, ${formData.postalCode || ''}`

    if (formData.Cliente_TIPO === 'Pessoa Jurídica' || formData.Cliente_TIPO === 'Condomínio') {
      params.append('Cliente_UserName', formData.Cliente_RZC2 || '')
      params.append('Cliente_RZC2', formData.Cliente_RZC2 || '')
      params.append('Cliente_CNPJC2', formData.Cliente_CNPJC2 || '')
    }

    params.append('Cliente_ENDC2', address.trim())

    if (formData.Cliente_TIPO === 'Pessoa Física') {
      const Cliente_NPECC2 = `${formData.nacionalidade || ''}, ${formData.profissao || ''}, ${formData.estadoCivil || ''}`
      params.append('Cliente_UserName', formData.Cliente_REPC2 || '')
      params.append('Cliente_NPECC2', Cliente_NPECC2)
    }

    // Representante
    params.append('Cliente_REPC2', formData.Cliente_REPC2 || '')
    params.append('Cliente_CPFREPC2', formData.Cliente_CPFREPC2 || '')
    params.append('Cliente_EMAILREPC2', formData.Cliente_EMAILREPC2 || '')
    params.append('Cliente_Email', formData.Cliente_EMAILREPC2 || '')
    params.append('Cliente_TELREPC2', formData.Cliente_TELREPC2 || '')

    // Responsável X Representante
    if (formData.Cliente_Responsavel_Representante === 'Sim') {
      params.append('Cliente_RESC2', formData.Cliente_REPC2 || '')
      params.append('Cliente_EMAILRESC2', formData.Cliente_EMAILREPC2 || '')
    } else {
      params.append('Cliente_RESC2', formData.Cliente_RESC2 || '')
      params.append('Cliente_EMAILRESC2', formData.Cliente_EMAILRESC2 || '')
    }

    // UCs (Unidade Consumidora)
    if (formData.ucs) {
      for (let i = 1; i <= 9; i++) {
        const ucKey = `Cliente_UC${i}` as never
        if (formData.ucs[ucKey]) {
          params.append(ucKey, formData.ucs[ucKey] as string)
          params.append(`Cliente_CM${i}`, (formData.ucs[`Cliente_CM${i}`] as string) || '')

          // Concatena Grupo e Subgrupo em Cliente_GS
          const grupo = formData.ucs[`Cliente_Grupo${i}`] || ''
          const subgrupo = formData.ucs[`Cliente_Subgrupo${i}`] || ''
          const gsValue = `${grupo}-${subgrupo}`.replace(/^-|-$/, '') // Remove traços no início ou fim, se valores estiverem vazios
          params.append(`Cliente_GS${i}`, gsValue)

          params.append(`Cliente_MT${i}`, (formData.ucs[`Cliente_MT${i}`] as string) || '')
        }
      }
    }

    // Tipo de Cliente
    let tipoCliente: string | undefined

    if (formData.Cliente_TIPO === 'Pessoa Física') {
      tipoCliente = formData.Cliente_TIPO
    }
    if (formData.Cliente_TIPO === 'Condomínio') {
      tipoCliente = 'Ente despersonalizado'
    }

    if (formData.Cliente_TIPO === 'Pessoa Jurídica') {
      tipoCliente = formData.Cliente_TIPO_Entidade
    }

    params.append('Cliente_TIPO', tipoCliente || '')

    // Montar a URL completa e redirecionamento e depois faz a chamada na nossa api interna para gerar o lead.
    const url = `${baseUrl}?${params.toString()}`

    if (url) {
      window.open(url, '_blank')
      try {
        setLoadingEnvelopDocusign(true)
        await postIdEnvelopDocSignToSalesForce(leadId, formData.Cliente_EMAILREPC2 as string)
        setSuccessDocusing(true)
      } catch (e) {
        const errorToLog = e instanceof Error ? e : new Error(String(e))
        logToServer(
          'error',
          `Falha ao gerar o envelope Docusign. LeadId: ${leadId}`,
          formData,
          errorToLog
        )
        setError(true)
        setSuccessDocusing(false)
      } finally {
        setLoadingEnvelopDocusign(false)
      }
    }
  }

  // usado para marcar o checkbox ao aceitar os termos dentro do modal dos termos de uso
  const handleCheck = () => {
    // Simula um evento de mudança no checkbox
    handleInputChange({
      target: {
        name: 'tdo',
        checked: true,
        value: 'true'
      }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  // Usado para ativar ou desativar o botão de enviar
  useEffect(() => {
    if (
      !formData?.Cliente_TIPO ||
      (formData.Cliente_TIPO === 'Pessoa Jurídica' && !formData.Cliente_TIPO_Entidade) ||
      !formData?.Cliente_Estado ||
      (formData.Cliente_TIPO === 'Pessoa Jurídica' && !formData?.Cliente_RZC2) ||
      (formData.Cliente_TIPO === 'Condomínio' && !formData?.Cliente_RZC2) ||
      (formData.Cliente_TIPO === 'Pessoa Jurídica' && !formData?.Cliente_CNPJC2) ||
      (formData.Cliente_TIPO === 'Condomínio' && !formData?.Cliente_CNPJC2) ||
      (formData.Cliente_TIPO === 'Pessoa Física' && !formData?.nacionalidade) ||
      (formData.Cliente_TIPO === 'Pessoa Física' && !formData?.profissao) ||
      (formData.Cliente_TIPO === 'Pessoa Física' && !formData?.estadoCivil) ||
      (formData.Cliente_TIPO === 'Pessoa Física' && !formData?.Cliente_NASCIMENTO) ||
      !formData.street ||
      !formData.city ||
      !formData.postalCode ||
      !formData.Cliente_REPC2 ||
      !formData.Cliente_CPFREPC2 ||
      !formData.Cliente_EMAILREPC2 ||
      !formData.Cliente_TELREPC2 ||
      !formData.Cliente_Responsavel_Representante ||
      !formData.ucs?.Cliente_UC1 ||
      !formData.ucs?.Cliente_CM1 ||
      //!formData.ucs?.Cliente_Grupo1 ||
      !formData.ucs?.Cliente_Subgrupo1 ||
      !formData.tdo ||
      loading ||
      (step == 2 && !files.length)
    ) {
      setDisabled(true)
    } else {
      setDisabled(false)
    }
  }, [loading, formData, files])

  // Usado para mostrar uma mensagem de alerta ao tentar fechar a aba, quando dados em processamento
  useEffect(() => {
    // Adiciona o evento apenas enquanto está processando
    if (processing) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }

    // Limpa o evento ao desmontar o componente
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [processing])

  return (
    <>
      {error && !processing && (
        <div className="mx-auto w-full items-center gap-6 rounded-md bg-white px-12 py-14 text-center shadow-lg">
          <h5 className="mb-4 text-2xl text-red-500">Erro ao processar dados!</h5>
          <p className="text-lg">Por favor, atualize a página e tente novamente.</p>
          <p className="text-lg">Caso o erro persistir, entre em contato conosco.</p>
        </div>
      )}

      {processing && !success && (
        <div
          ref={processingDataRef}
          className="mx-auto mt-40 w-full items-center gap-6 rounded-md bg-white px-12 py-14 text-center shadow-lg"
        >
          <h3 className="mb-4 text-3xl">Estamos processando seus dados, por favor aguarde.</h3>
          <br />
          <p className="text-red-500">
            <strong>
              ATENÇÃO: mantenha essa aba (janela) aberta até a conclusão do processamento de dados!
            </strong>
          </p>
          <FakeProgress ref={progressBarRef} />
        </div>
      )}

      {success && !processing && (
        <div className="mx-auto w-full items-center gap-6 rounded-md bg-white px-12 py-14 text-center shadow-lg">
          {successDocusing === null && (
            <>
              <h5 className="mb-4 text-2xl">Arquivos enviados com sucesso. Estamos quase lá!</h5>
              <p className="mb-3 text-center text-lg">Para concluir, clique no botão abaixo:</p>
              <button
                disabled={loadingEnvelopDocusign}
                onClick={() => handleGenerateUrl()}
                className="btn-primaty btn btn-success btn-lg btn-block uppercase text-white"
              >
                {loadingEnvelopDocusign ? 'Processando...' : 'Clique aqui para prosseguir'}
              </button>
              <br />
              <br />
              <p className="text-gray-500">Ambiente 100% seguro e protegido pela Docusign.</p>
              <br />
            </>
          )}

          {successDocusing === true && (
            <>
              <h5 className="mb-4 text-2xl">Agora, prossiga na nova aba aberta</h5>
              <p className="text-center">
                Você também receberá um e-mail para finalizar o processo de adesão.
              </p>
              <br />
            </>
          )}
        </div>
      )}

      {/* Debug */}
      {/*<div
        style={{ overflow: 'auto' }}
        className="fixed left-0 top-0 z-[100] h-screen w-1/5 overflow-auto truncate bg-zinc-800 p-6 text-white"
      >
        <pre style={{ whiteSpace: 'break-spaces' }}>{JSON.stringify(formData, null, 2)}</pre>
      </div>*/}

      {!success && !processing && (
        <div
          ref={form}
          className="mx-auto flex w-full flex-col items-center gap-6 rounded-xl bg-white px-6 py-8"
        >
          <form
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onSubmit={handleSubmit}
            className="flex w-full flex-col gap-6"
          >
            {step === 1 && (
              <div>
                <div className="text-bold flex justify-center gap-3 pb-5 text-xl">
                  <input
                    type="checkbox"
                    checked={formData.tdo}
                    name="tdo"
                    id="tdo"
                    onChange={handleInputChange}
                  />
                  <label style={{ fontWeight: 'bold' }} htmlFor="tdo" className="text-bold">
                    Declaro que li e aceito os{' '}
                    <label htmlFor="modal_tos" className="text-blue-500 hover:cursor-pointer">
                      Termos de Uso
                    </label>
                    .
                  </label>
                </div>

                {/* Seleção dos dados iniciais: Tipo de Cliente, Entidade e Estado */}
                <InitialFields formData={formData} onChange={handleInputChange} loading={loading} />

                {formData?.Cliente_TIPO === 'Pessoa Física' && (
                  <PfFields formData={formData} loading={loading} onChange={handleInputChange} />
                )}
                {(formData?.Cliente_TIPO === 'Pessoa Jurídica' ||
                  formData?.Cliente_TIPO === 'Condomínio') && (
                  <PjFields formData={formData} loading={loading} onChange={handleInputChange} />
                )}

                {/* Modal Adição de Unidades Consumidoras */}
                {formData?.Cliente_TIPO && (
                  <ModalConsumerUnit
                    formData={formData}
                    onChange={handleInputChange}
                    onDelete={(data) => setFormData(data)}
                  />
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                {formData?.Cliente_TIPO && (
                  <MultiFileUpload
                    title="Documentos necessários para adesão"
                    onFiles={setFiles}
                    loading={loading}
                  >
                    <p className="text-center font-bold">
                      Por gentileza, insira os documentos listados abaixo. Caso não tenha alguns dos
                      documentos, não se preocupe, nossa equipe fará a analise e entrará em contato
                      solicitando informações faltantes.
                    </p>
                    <p className="text-center font-bold">
                      <ul>
                        {formData?.Cliente_TIPO === 'Pessoa Física' && (
                          <>
                            <li>• Documento Pessoal com RG, Órgão Expedidor e CPF (RG ou CNH)</li>
                            <li>
                              • Fatura(s) de energia elétrica da(s) Unidade(s) Consumidora(s).
                            </li>
                          </>
                        )}
                        {formData?.Cliente_TIPO === 'Pessoa Jurídica' && (
                          <>
                            <li>• Contrato Social Atualizado</li>
                            <li>• Ata de Eleição de Diretores, quando for o caso</li>
                            <li>
                              • Documento pessoal do sócio administrador (assinante do contrato)
                            </li>
                          </>
                        )}
                        {formData?.Cliente_TIPO === 'Condomínio' && (
                          <>
                            <li>• Estatuto Social</li>
                            <li>• Ata de Eleição dos Síndicos (administradores)</li>
                            <li>• Documento pessoal dos síndicos (administradores)</li>
                          </>
                        )}
                      </ul>
                    </p>
                  </MultiFileUpload>
                )}
              </div>
            )}

            {/* Aviso de que os termos de uso não foram aceitos */}
            {!formData.tdo && (
              <p className="text-center text-red-500">
                Você precisa aceitar os{' '}
                <a className="text-blue-500" href="#economizar">
                  Termos de Uso
                </a>{' '}
                para aderir o serviço.
              </p>
            )}

            {/* Botão para Enviar para API */}
            <button
              disabled={disabled}
              className="btn-primaty btn btn-success btn-lg btn-block text-white"
            >
              {loading ? (
                <>Processando...</>
              ) : (
                <span>{step === 1 ? 'Próximo Passo' : 'Quero aderir agora'}</span>
              )}
            </button>

            <p className="text-center font-bold text-red-600">{errorApi}</p>

            {message && <p className="text-center font-bold text-red-600">{message}</p>}

            {/* Modal Minuta */}
            <ModalMinuta
              handleSendData={handleOkPreviewMinuta}
              loading={loading}
              disabled={disabled}
            />

            {/* Modal Termos */}
            <ModalTos onClick={() => handleCheck()} />
          </form>
        </div>
      )}

      {/* Modal Docusign */}
      <ModalDocusign src={docusignUrl} />
    </>
  )
}

export default Form
